import { useState } from 'react';
import {
  Banknote,
  CalendarClock,
  FileStack,
  Pencil,
  Plus,
  TrendingUp,
  TriangleAlert,
  Wallet,
} from 'lucide-react';
import {
  getApiError,
  useArchiveFeeStructureMutation,
  useGetFeeSummaryQuery,
  useListFeeStructuresQuery,
  useListGroupsQuery,
  useListInvoicesQuery,
  useRaiseInvoicesMutation,
  type FeeStructure,
  type InvoiceRow,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Select } from '@/components/ui/Field';
import { PageLayout } from '@/components/ui/PageLayout';
import { Pagination } from '@/components/ui/Pagination';
import { SearchField } from '@/components/ui/SearchField';
import { StatTile } from '@/components/ui/StatTile';
import { formatMinor, termLabel } from '@/lib/money';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { InvoiceDialog } from './InvoiceDialog';
import { StructureDialog, emptyStructureDraft, toDraft, type StructureDraft } from './StructureDialog';

const PAGE_SIZE = 20;

/*
  Fees.

  ── The order of this screen is the order of the work ──────────────────────

  A bursar arrives with one of three questions: how much is still owed, who has
  not paid, and has this term been billed yet. So the figures come first, the
  structures (which answer the third) sit as a strip rather than a screen of
  their own, and the invoice list — the thing actually worked through — gets the
  rest of the page.

  ── Why "outstanding" is the default filter ────────────────────────────────

  A paid invoice needs nothing from anybody. Opening on every invoice ever
  raised buries the twenty that need chasing under the nine hundred that do not.
*/
export function FeesPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('outstanding');
  const [groupId, setGroupId] = useState('');

  const [structureDraft, setStructureDraft] = useState<StructureDraft | null>(null);
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  const [pendingArchive, setPendingArchive] = useState<FeeStructure | null>(null);
  const [raiseTarget, setRaiseTarget] = useState<FeeStructure | null>(null);
  const [banner, setBanner] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const { data: groups = [] } = useListGroupsQuery(institutionId, { skip: !institutionId });
  const { data: structures = [], isLoading: structuresLoading } = useListFeeStructuresQuery(institutionId, {
    skip: !institutionId,
  });
  const { data: summary } = useGetFeeSummaryQuery({ institutionId }, { skip: !institutionId });

  const {
    data: invoices,
    isLoading,
    isFetching,
    isError,
    error,
  } = useListInvoicesQuery(
    {
      institutionId,
      page,
      pageSize: PAGE_SIZE,
      search: search.trim() || undefined,
      status: (status || undefined) as InvoiceRow['status'] | 'outstanding' | 'overdue' | undefined,
      groupId: groupId || undefined,
    },
    { skip: !institutionId },
  );

  const [raiseInvoices, { isLoading: raising }] = useRaiseInvoicesMutation();
  const [archiveStructure, { isLoading: archiving }] = useArchiveFeeStructureMutation();

  /* Any filter change puts you back on page 1 — page 7 of the old result set is
     meaningless against the new one. */
  const onFilter = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const handleRaise = async () => {
    if (!raiseTarget) return;
    try {
      const result = await raiseInvoices({ institutionId, structureId: raiseTarget._id }).unwrap();
      setBanner({
        tone: 'success',
        text:
          result.raised === 0
            ? `Every student covered by "${raiseTarget.name}" was already invoiced — nothing was billed twice.`
            : `${result.raised} invoice${result.raised === 1 ? '' : 's'} raised for "${raiseTarget.name}", GHS ${formatMinor(result.totalBilledMinor)} billed${result.skipped ? ` (${result.skipped} already had one)` : ''}.`,
      });
      setRaiseTarget(null);
    } catch (err) {
      setBanner({ tone: 'danger', text: getApiError(err) });
      setRaiseTarget(null);
    }
  };

  const handleArchive = async () => {
    if (!pendingArchive) return;
    try {
      await archiveStructure({ institutionId, structureId: pendingArchive._id }).unwrap();
      setPendingArchive(null);
    } catch (err) {
      setBanner({ tone: 'danger', text: getApiError(err) });
      setPendingArchive(null);
    }
  };

  const columns: Column<InvoiceRow>[] = [
    {
      key: 'invoice',
      header: 'Invoice',
      render: (inv) => (
        <div className="min-w-0">
          <p className="font-semibold text-foreground">{inv.studentName}</p>
          <p className={styles.meta}>
            {inv.invoiceNumber}
            {inv.groupName && ` · ${inv.groupName}`}
          </p>
        </div>
      ),
    },
    {
      key: 'term',
      header: 'Term',
      render: (inv) => (
        <span className="text-muted-foreground">
          {termLabel(inv.term)}
          <span className="opacity-60"> · {inv.academicYear}</span>
        </span>
      ),
    },
    {
      key: 'due',
      header: 'Due',
      render: (inv) => (
        <span className={cn('tabular-nums', inv.isOverdue ? 'text-[hsl(var(--record-ink-danger))]' : 'text-muted-foreground')}>
          {inv.dueDate}
        </span>
      ),
    },
    { key: 'total', header: 'Billed', align: 'num', render: (inv) => <span className={styles.figure}>{formatMinor(inv.totalMinor)}</span> },
    {
      key: 'paid',
      header: 'Paid',
      align: 'num',
      render: (inv) => (
        <span className={cn(styles.figure, inv.paidMinor > 0 && 'text-[hsl(var(--record-ink-success))]')}>
          {formatMinor(inv.paidMinor)}
        </span>
      ),
    },
    {
      key: 'balance',
      header: 'Balance',
      align: 'num',
      render: (inv) => (
        <span className={cn(styles.figure, inv.balanceMinor > 0 && 'text-[hsl(var(--record-ink-danger))]')}>
          {formatMinor(inv.balanceMinor)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (inv) =>
        inv.status === 'paid' ? (
          <Mark tone="success">Paid</Mark>
        ) : inv.status === 'cancelled' ? (
          <Mark tone="neutral">Cancelled</Mark>
        ) : inv.isOverdue ? (
          <Mark tone="danger">Overdue</Mark>
        ) : inv.status === 'part_paid' ? (
          <Mark tone="warning">Part paid</Mark>
        ) : (
          <Mark tone="neutral">Unpaid</Mark>
        ),
    },
  ];

  return (
    <PageLayout
      title="Fees"
      subtitle="Invoices, payments and what is still outstanding"
      breadcrumbs={[{ label: 'Finance' }, { label: 'Fees' }]}
      contained={false}
      actions={
        <Button icon={<Plus size={13} />} onClick={() => setStructureDraft(emptyStructureDraft())}>
          New structure
        </Button>
      }
      toolbar={
        <>
          <SearchField
            value={search}
            onChange={onFilter(setSearch)}
            placeholder="Search by student name"
            className="min-w-[200px] flex-1"
          />
          <Select value={status} onChange={(e) => onFilter(setStatus)(e.target.value)} className="w-auto">
            <option value="outstanding">Outstanding</option>
            <option value="overdue">Overdue</option>
            <option value="unpaid">Unpaid</option>
            <option value="part_paid">Part paid</option>
            <option value="paid">Paid</option>
            <option value="">All invoices</option>
          </Select>
          <Select value={groupId} onChange={(e) => onFilter(setGroupId)(e.target.value)} className="w-auto">
            <option value="">All groups</option>
            {groups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </Select>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {banner && (
          <div
            className={cn(
              banner.tone === 'success' ? styles.markSuccess : styles.markDanger,
              'w-full justify-between normal-case tracking-normal text-[11px]',
            )}
          >
            <span className="flex items-center gap-1.5">
              {banner.tone === 'danger' && <TriangleAlert size={12} className="flex-shrink-0" />}
              {banner.text}
            </span>
            <button type="button" onClick={() => setBanner(null)} className="font-bold opacity-60 hover:opacity-100">
              Dismiss
            </button>
          </div>
        )}

        {/* ── The figures ───────────────────────────────────────────────── */}
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label="Billed" value={`GHS ${formatMinor(summary?.billedMinor ?? 0)}`} icon={<FileStack size={14} />} tone="info" hint={`${summary?.invoiceCount ?? 0} invoices`} />
          <StatTile label="Collected" value={`GHS ${formatMinor(summary?.collectedMinor ?? 0)}`} icon={<TrendingUp size={14} />} tone="success" hint={`${summary?.paidCount ?? 0} settled`} />
          <StatTile label="Outstanding" value={`GHS ${formatMinor(summary?.outstandingMinor ?? 0)}`} icon={<Wallet size={14} />} tone="warning" />
          <StatTile label="Overdue" value={summary?.overdueCount ?? 0} icon={<CalendarClock size={14} />} tone={summary?.overdueCount ? 'danger' : 'neutral'} hint="Past the due date" />
        </div>

        {/* ── Structures ────────────────────────────────────────────────── */}
        <div className={styles.panel}>
          <div className="flex items-center justify-between px-4 py-2.5">
            <p className={styles.sectionTitle}>Fee structures</p>
            <span className={styles.meta}>{structures.length} active</span>
          </div>

          {structuresLoading ? (
            <div className={cn(styles.panelRow, 'text-[11px] text-muted-foreground')}>Loading…</div>
          ) : structures.length === 0 ? (
            <div className={cn(styles.panelRow, 'flex flex-wrap items-center justify-between gap-2')}>
              <p className={styles.meta}>
                No fee structure yet. One defines what a class owes for a term; invoices are raised from it.
              </p>
              <Button size="sm" variant="secondary" icon={<Plus size={12} />} onClick={() => setStructureDraft(emptyStructureDraft())}>
                Create one
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border border-t border-border">
              {structures.map((s) => (
                <div key={s._id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold text-foreground">{s.name}</p>
                    <p className={styles.meta}>
                      {termLabel(s.term)} · {s.academicYear} ·{' '}
                      {s.groupId ? groups.find((g) => g._id === s.groupId)?.name ?? 'One group' : 'All groups'} · due {s.dueDate}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <span className={cn(styles.figure, 'text-[12.5px]')}>GHS {formatMinor(s.totalMinor)}</span>
                    <Button size="sm" variant="secondary" onClick={() => setRaiseTarget(s)}>
                      Raise invoices
                    </Button>
                    <Button size="sm" variant="ghost" iconOnly aria-label={`Edit ${s.name}`} onClick={() => setStructureDraft(toDraft(s))}>
                      <Pencil size={13} />
                    </Button>
                    <Button size="sm" variant="ghost" iconOnly aria-label={`Archive ${s.name}`} onClick={() => setPendingArchive(s)}>
                      <Banknote size={13} className="rotate-90 opacity-70" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Invoices ──────────────────────────────────────────────────── */}
        <div className={cn(styles.panel, 'overflow-clip')}>
          <DataTable
            columns={columns}
            rows={invoices?.items ?? []}
            rowKey={(row) => row._id}
            loading={isLoading || isFetching}
            error={isError ? error : undefined}
            errorMessage={isError ? getApiError(error) : undefined}
            onRowClick={(row) => setOpenInvoiceId(row._id)}
            emptyTitle={search || groupId || status !== 'outstanding' ? 'No invoices match these filters' : 'Nothing outstanding'}
            emptyDescription={
              structures.length === 0
                ? 'Create a fee structure and raise invoices from it to start billing.'
                : search || groupId || status !== 'outstanding'
                  ? 'Try a different status, group, or name.'
                  : 'Every invoice raised so far has been settled.'
            }
            footer={
              invoices && invoices.total > 0 ? (
                <Pagination
                  page={invoices.page}
                  totalPages={invoices.totalPages}
                  total={invoices.total}
                  pageSize={invoices.pageSize}
                  onPageChange={setPage}
                />
              ) : undefined
            }
          />
        </div>
      </div>

      <StructureDialog
        draft={structureDraft}
        groups={groups}
        institutionId={institutionId}
        onClose={() => setStructureDraft(null)}
      />

      <InvoiceDialog
        invoiceId={openInvoiceId}
        institutionId={institutionId}
        onClose={() => setOpenInvoiceId(null)}
      />

      <ConfirmDialog
        open={raiseTarget !== null}
        onClose={() => setRaiseTarget(null)}
        onConfirm={handleRaise}
        loading={raising}
        title="Raise invoices"
        confirmLabel="Raise invoices"
        body={
          raiseTarget ? (
            <>
              Bill every active student in{' '}
              <strong>
                {raiseTarget.groupId ? groups.find((g) => g._id === raiseTarget.groupId)?.name ?? 'the group' : 'the whole school'}
              </strong>{' '}
              GHS {formatMinor(raiseTarget.totalMinor)} for {termLabel(raiseTarget.term)}.
              <br />
              <br />
              Students who already have an invoice from this structure are skipped, so running
              this twice cannot bill anybody twice.
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={pendingArchive !== null}
        onClose={() => setPendingArchive(null)}
        onConfirm={handleArchive}
        loading={archiving}
        destructive
        title="Archive fee structure"
        confirmLabel="Archive"
        body={
          <>
            <strong>{pendingArchive?.name}</strong> will no longer be available for raising invoices.
            <br />
            <br />
            Invoices already raised from it, and every payment against them, are left exactly as
            they are — this stops it being used again, it does not unbill the term.
          </>
        }
      />
    </PageLayout>
  );
}
